/**
 * Canvas Panning Hook
 * Handles mouse drag to pan the canvas viewport
 */

import { RefObject, useEffect, useRef } from 'react'

interface PanState {
  isPanning: boolean
  startX: number
  startY: number
}

/**
 * Hook to enable panning on a canvas element
 * @param canvasRef - Reference to the canvas element
 * @param onPan - Callback function called with delta x and y
 */
export function useCanvasPanning(
  canvasRef: RefObject<HTMLCanvasElement>,
  onPan: (dx: number, dy: number) => void
) {
  const panStateRef = useRef<PanState>({
    isPanning: false,
    startX: 0,
    startY: 0,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleMouseDown = (e: MouseEvent) => {
      // Only start panning on left mouse button
      if (e.button !== 0) return

      panStateRef.current = {
        isPanning: true,
        startX: e.clientX,
        startY: e.clientY,
      }

      // Change cursor to indicate panning
      canvas.style.cursor = 'grabbing'
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!panStateRef.current.isPanning) return

      const dx = e.clientX - panStateRef.current.startX
      const dy = e.clientY - panStateRef.current.startY

      // Update start position for next frame
      panStateRef.current.startX = e.clientX
      panStateRef.current.startY = e.clientY

      // Call the pan callback
      onPan(dx, dy)
    }

    const handleMouseUp = () => {
      if (panStateRef.current.isPanning) {
        panStateRef.current.isPanning = false
        canvas.style.cursor = 'grab'
      }
    }

    const handleMouseLeave = () => {
      // Stop panning if mouse leaves the canvas
      if (panStateRef.current.isPanning) {
        panStateRef.current.isPanning = false
        canvas.style.cursor = 'grab'
      }
    }

    // Set initial cursor
    canvas.style.cursor = 'grab'

    // Add event listeners
    canvas.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    canvas.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      canvas.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [canvasRef, onPan])
}
